import { useEffect, useState, useCallback, useMemo } from 'react';
import CollapsibleTable from '../../components/table';
import ModalProveedores from '../../components/ModalProveedores';
import DoubleConfirmModal from '../../components/DoubleConfirmModal';
import { useSession } from '../../components/hooks/useSession';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import config from '../../config/config';
import axios from 'axios';

import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import BotonAgregar from '../../components/BotonAgregar';

import ExportButton from '../../components/BotonEx';
import { Box } from '@mui/material';

// Helpers sesión
const getSessionString = (key) => sessionStorage.getItem(key) || '';

const getSessionArray = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('Error parseando', key, e);
    return [];
  }
};

// Normaliza idEmpresa que puede venir como:
//  - [2]
//  - "2"
//  - "[1,2,3]"
const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];

  if (Array.isArray(idEmpresa)) {
    return idEmpresa.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }

  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();

    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) {
          return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
        }
      } catch {}
    }

    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }

  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

export default function Proveedores() {
  const apiUrl = config.apiBaseUrl;

  // useSession se mantiene por compatibilidad
  useSession();

  // ─── Datos de sesión reactivos ────────────────────────────────────────
  const [loggedInUsername, setLoggedInUsername] = useState(getSessionString('loggedInUsername'));
  const [idEmpresa, setIdEmpresa] = useState(getSessionArray('EmpresaId'));

  // UsuarioId para persistencia
  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);

  // Key estable
  const empresaIdsKey = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length ? arr.join(',') : '';
  }, [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaIdsKey) return [];
    return empresaIdsKey
      .split(',')
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaIdsKey]);

  // Para preferencias: TODAS => 0
  const empresaIdPref = useMemo(() => {
    return empresaIds.length > 1 ? 0 : (empresaIds[0] ?? 0);
  }, [empresaIds]);

  // Escuchar cambio de empresa
  useEffect(() => {
    const handleEmpresaChange = () => {
      const nuevosIds = getSessionArray('EmpresaId');
      const nuevoUser = getSessionString('loggedInUsername');

      setIdEmpresa(nuevosIds);
      setLoggedInUsername(nuevoUser);
    };

    window.addEventListener('empresaCambiada', handleEmpresaChange);
    return () => window.removeEventListener('empresaCambiada', handleEmpresaChange);
  }, []);

  const [data, setData] = useState([]);
  const [formData, setFormData] = useState({
    CENTRO_COSTO: '',
    NUMERO_CC: '',
    IDEMPRESA: ''
  });
  const [selectedCC, setSelectedCC] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: '',
    action: '',
    row: null,
  });

  // ────── Obtener datos ──────
  const fetchData = useCallback(() => {
    if (!apiUrl) return;
    if (!empresaIdsKey) return;

    axios
      .post(`${apiUrl}/proveedoresTabla`, { idEmpresa: empresaIds })
      .then((res) => {
        if (res.data.mensaje) {
          handleOpenSweetAlert(res.data.mensaje, 'error');
          setData([]);
        } else {
          setData(res.data);
        }
      })
      .catch((err) => {
        const mensajeError =
          err.response?.data?.error ||
          err.response?.data?.mensaje ||
          'Error al procesar la solicitud';
        handleOpenSweetAlert(mensajeError, 'error');
      });
  }, [apiUrl, empresaIds, empresaIdsKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ────── Modal ──────
  const abrirModal = useCallback((row = null) => {
    if (row) {
      // EDITAR → usar siempre la empresa del registro
      setSelectedCC(row);
      setFormData({
        CENTRO_COSTO: row.NOMBRE || '',
        NUMERO_CC: row.NUMERO || '',
        IDEMPRESA: row.IDEMPRESA || '',
      });
    } else {
      // NUEVO
      let empresaDefault = '';

      if (empresaIds.length === 1) {
        empresaDefault = empresaIds[0];
      } else {
        empresaDefault = '';
      }

      setSelectedCC(null);
      setFormData({
        CENTRO_COSTO: '',
        NUMERO_CC: '',
        IDEMPRESA: empresaDefault,
      });
    }

    setModalOpen(true);
  }, [empresaIds]);

  const cerrarModal = () => {
    setModalOpen(false);
    setSelectedCC(null);
    setFormData({
      CENTRO_COSTO: '',
      NUMERO_CC: '',
      IDEMPRESA: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ────── Activar / Desactivar ──────
  const cambiarEstadoCentroCosto = useCallback(async (row) => {
    const nuevoEstado = row.ACTIVO ? 0 : 1;
    try {
      await axios.post(`${apiUrl}/desactivarCentroCosto`, {
        CCID: row.ID_UNICO,
        ACTIVO: nuevoEstado,
        SESSION: loggedInUsername
      });
      handleOpenSweetAlert(
        nuevoEstado ? 'Centro de costo activado' : 'Centro de costo desactivado',
        'success'
      );
      fetchData();
    } catch (err) {
      handleOpenSweetAlert('Error al cambiar el estado del centro de costo', 'error');
    }
  }, [apiUrl, loggedInUsername, fetchData]);

  const openConfirmation = useCallback((action, row, message) => {
    setConfirmModal({ open: true, action, row, message });
  }, []);

  const handleConfirmAction = () => {
    if (confirmModal.action === 'cambiarEstado') {
      cambiarEstadoCentroCosto(confirmModal.row);
    }
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  };

  const handleCancelAction = () => {
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  };

  // ────── Botones ──────
  const buttons = useMemo(() => [
    {
      id: 1,
      label: 'Editar',
      onClick: abrirModal,
      icon: <EditIcon />,
      buttonColor: '#2196f3',
      buttonFontColor: '#ffffff',
      isVisible: true,
    },
    {
      id: 2,
      label: (row) => row.ACTIVO ? 'Desactivar' : 'Activar',
      onClick: (row) => {
        const mensaje = row.ACTIVO
          ? '¿Está seguro de desactivar este centro de costo?'
          : '¿Está seguro de activar este centro de costo?';
        openConfirmation('cambiarEstado', row, mensaje);
      },
      icon: (row) => row.ACTIVO ? <BlockIcon /> : <CheckCircleIcon />,
      buttonColor: (row) => row.ACTIVO ? '#e53935' : '#43a047',
      buttonFontColor: '#ffffff',
      isVisible: true,
    },
  ], [abrirModal, openConfirmation]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
      <CollapsibleTable
        buttons={buttons}
        dataTable={data}
        showExportButton={true}
        hiddenFields={['IDEMPRESA', 'ID_UNICO']}
        rightButtons={
          <ExportButton
            data={data}
            fileName="Proveedores.xlsx"
            hiddenFields={['IDEMPRESA', 'ID_UNICO']}
          />
        }
        leftButtons={
          <BotonAgregar
            title="Agregar Usuario"
            onClick={() => abrirModal(null)}
          />
        }

        persistEnabled
        persistKey="Proveedores"
        persistVista="layout"
        usuarioId={usuarioId}
        empresaId={empresaIdPref}
        apiUrl={apiUrl}
      />
      </Box>

      {/*
        OJO:
        En tu código original importas ModalProveedores pero no lo renderizabas.
        Te lo dejo comentado porque no sé qué props reales espera ese modal.
        Si quieres, me pegas su interfaz/props y te lo conecto bien.
      */}
      {/*
      <ModalProveedores
        open={modalOpen}
        onClose={cerrarModal}
        onSave={fetchData}
        proveedorId={selectedCC?.ID_UNICO}
        formData={formData}
        handleInputChange={handleInputChange}
        usuario={loggedInUsername}
        empresaId={empresaIds}
      />
      */}

      <DoubleConfirmModal
        open={confirmModal.open}
        message={confirmModal.message}
        handleConfirm={handleConfirmAction}
        handleCancel={handleCancelAction}
      />
    </Box>
  );
}